import { ShieldCheckIcon } from '@heroicons/react/24/outline'
import { useCallback, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import type { AccountSecurity } from '@/api'
import { SettingsRow } from '@/components/SettingsRow'
import { SettingsSection } from '@/components/SettingsSection'

import { AuthenticatorMethod } from './authenticator/AuthenticatorMethod'
import { EmailMethod } from './email/EmailMethod'
import { PasswordMethod } from './password/PasswordMethod'

export type LocalMethodProps = {
    disabled: boolean
    runAction: (action: () => Promise<void>) => Promise<void>
}

export const LocalSecuritySection = ({ security }: { security: AccountSecurity | undefined }) => {
    const { t } = useTranslation()
    const [busy, setBusy] = useState(false)
    const busyRef = useRef(false)

    const runAction = useCallback<LocalMethodProps['runAction']>(async (action) => {
        if (busyRef.current) {
            return
        }

        busyRef.current = true
        setBusy(true)

        try {
            await action()
        } finally {
            busyRef.current = false
            setBusy(false)
        }
    }, [])

    return (
        <SettingsSection title={t('security.local.title')}>
            {security?.credentialSource === 'external' ? (
                <SettingsRow
                    icon={<ShieldCheckIcon className="h-5 w-5" aria-hidden="true" />}
                    title={t('security.local.externalCredentials.title')}
                    description={t('security.local.externalCredentials.description')}
                />
            ) : (
                <>
                    <PasswordMethod password={security?.password} disabled={busy} runAction={runAction} />
                    <AuthenticatorMethod mfa={security?.mfa} disabled={busy} runAction={runAction} />
                    <EmailMethod mfa={security?.mfa} disabled={busy} runAction={runAction} />
                </>
            )}
        </SettingsSection>
    )
}
